import { Divider, Grid2, Typography } from '@mui/material'
import { ReviewPaymentDataFee } from 'api/payments/types'
import { useAuth } from 'components/AuthProvider'
import Button from 'components/Button'
import { PaymentsProvider } from 'components/PaymentsProvider'
import { walletPath } from 'components/Routes'
import TransactionButtons from 'components/TransactionButtons/TransactionButtons'
import DefaultLayout from 'components/layout/DefaultLayout/DefaultLayout'
import { ReviewOrder } from 'api/trading'
import { useCallback, useMemo, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { formattedPrice } from 'utils/util'
import useStyles from './styles'

export interface Props {
  reviewOrderData?: ReviewOrder
  reviewOrderId: string
}

export const FEE_PLAN_TYPES: { [key: string]: string } = {
  INSURANCE: 'Standard Insurance Fee',
  TRANSACTION: 'Standard Transaction Fee'
}

export default function RechargeConfirm(): JSX.Element {
  const { classes } = useStyles()
  const { setToast } = useAuth()
  const navigate = useNavigate()

  const [disabledButton, setDisabledButton] = useState<boolean>(true)

  const { state } = useLocation()

  const { amount } = state ?? {}

  const [reviewPaymentDataFee, setReviewPaymentDataFee] =
    useState<ReviewPaymentDataFee | null>(null)

  const creditCardTotal = useMemo(() => {
    const { payment_amount, fee } = reviewPaymentDataFee ?? {}
    return Number(payment_amount) - Number(fee)
  }, [reviewPaymentDataFee])

  const handleConfirm = useCallback(async () => {
    if (!disabledButton) {
      const paymentForm = document.getElementById(
        'credit-or-debit-form'
      ) as HTMLFormElement
      if (paymentForm) {
        paymentForm.requestSubmit()
      }
    }
  }, [disabledButton])

  const handleRechargeSuccess = () => {
    setDisabledButton(false)
    setToast({
      open: true,
      message: 'Transfer successfully!',
      duration: 3000,
      type: 'success'
    })
    navigate(walletPath())
  }

  const formInformation = [
    {
      label: 'Credit Card',
      value: creditCardTotal,
      unit: '$'
    },
    {
      label: 'CC fee of',
      value: reviewPaymentDataFee?.fee,
      unit: '$'
    }
  ]

  const handleCreatePaymentSuccess = (
    reviewPaymentDataFee: ReviewPaymentDataFee
  ) => {
    setReviewPaymentDataFee({
      payment_amount: reviewPaymentDataFee.payment_amount,
      fee: reviewPaymentDataFee.fee
    })
  }

  return (
    <PaymentsProvider>
      <DefaultLayout
        wrapperContainer={{ px: 2, py: 4 }}
        wrapperContent={{ m: 0 }}
        showBackIcon
        heading='Recharge Confirm'
      >
        <Grid2>
          <Grid2 container>
            <Grid2>
              <Typography component='h2'>
                Recharge amount
                <b>{` $${amount} `}</b>
              </Typography>
            </Grid2>
          </Grid2>

          <Divider />

          {reviewPaymentDataFee && (
            <>
              <Grid2 container spacing={2}>
                {formInformation.map(info => (
                  <Grid2 container key={info.label}>
                    <Grid2>
                      <Typography className={classes.label}>
                        {info.label}
                      </Typography>
                    </Grid2>
                    <Grid2>
                      <Typography>
                        {info.unit + formattedPrice(info.value ?? '0')}
                      </Typography>
                    </Grid2>
                  </Grid2>
                ))}
              </Grid2>

              <Divider />

              <Grid2 container size={{ xs: 12 }}>
                <Grid2>
                  <Typography component='p' variant='body1' fontWeight='700'>
                    Total
                  </Typography>
                </Grid2>
                <Grid2>
                  <Typography component='p' variant='body1' fontWeight='700'>
                    ${reviewPaymentDataFee?.payment_amount}
                  </Typography>
                </Grid2>
              </Grid2>
            </>
          )}

          <Grid2 mt={4}>
            <TransactionButtons
              price={Number(amount)}
              setDisabledButton={setDisabledButton}
              handlePaymentSuccess={handleRechargeSuccess}
              handleCreatePaymentSuccess={handleCreatePaymentSuccess}
            />
          </Grid2>
          {!disabledButton && (
            <Grid2 flex={1}>
              <Button
                fullWidth
                type='button'
                color='primary'
                disabled={disabledButton}
                onClick={handleConfirm}
              >
                Confirm payment $
                {reviewPaymentDataFee?.payment_amount ?? amount}
              </Button>
            </Grid2>
          )}
        </Grid2>
      </DefaultLayout>
    </PaymentsProvider>
  )
}
